/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-10-13
 */

import i18n from '../config/i18n';

/**
 * Supported languages in the application
 */
export const SUPPORTED_LANGUAGES = {
  en: { code: 'en', name: 'English', nativeName: 'English' },
  es: { code: 'es', name: 'Spanish', nativeName: 'Español' },
  fr: { code: 'fr', name: 'French', nativeName: 'Français' },
} as const;

export type SupportedLanguageCode = keyof typeof SUPPORTED_LANGUAGES;

/**
 * Change the application language
 * @param languageCode - The language code to switch to
 */
export const changeLanguage = async (
  languageCode: SupportedLanguageCode
): Promise<void> => {
  await i18n.changeLanguage(languageCode);
  // Update HTML lang attribute for accessibility
  document.documentElement.lang = languageCode;
};

/**
 * Get the current language code
 * @returns The current language code
 */
export const getCurrentLanguage = (): SupportedLanguageCode => {
  const currentLang = i18n.language;
  // Handle language codes with region (e.g., 'en-US' -> 'en')
  const baseLanguage = currentLang.split('-')[0];
  return (
    baseLanguage in SUPPORTED_LANGUAGES ? baseLanguage : 'en'
  ) as SupportedLanguageCode;
};

/**
 * Get the current language display name
 * @returns The current language display name
 */
export const getCurrentLanguageName = (): string => {
  const currentLang = getCurrentLanguage();
  return SUPPORTED_LANGUAGES[currentLang].name;
};

/**
 * Get the current language native name
 * @returns The current language native name
 */
export const getCurrentLanguageNativeName = (): string => {
  const currentLang = getCurrentLanguage();
  return SUPPORTED_LANGUAGES[currentLang].nativeName;
};

/**
 * Check if a language code is supported
 * @param code - The language code to check
 * @returns True if the language is supported
 */
export const isLanguageSupported = (code: string): boolean =>
  code in SUPPORTED_LANGUAGES;
